import _ from "lodash";
import * as QueryString from "querystringify";
import React from "react";
import * as BS from "react-bootstrap";
import { connect } from "react-redux";
import { Link, RouteComponentProps, withRouter } from "react-router-dom";
import * as redux from "redux";
import * as yup from "yup";
import * as Actions from "../../../actions";
import * as Comp from "../../../components";
import { ComponentWithValidatedForm } from "../../../components/validation";
import { Paths } from "../../../constants";
import { Statuses } from "../../../domain";
import * as Forms from "../../../forms";
import * as M from "../../../models";
import { Api } from "../../../services/Api";
import { LegacyApi } from "../../../services/LegacyApi";
import { AppState } from "../../../store";
import { AuthHelper, Utility } from "../../../utils";

const mapStateToProps = (state: AppState, ownProps: OwnProps | undefined): ConnectedState => ({
    ...Comp.Base.DefaultStatePropsMap(state, ownProps),
    api: new Api(state),
    clientId: state.auth.clientId,
    clientSecret: state.auth.clientSecret,
    countries: state.countries.countries,
    isLoggedIn: (state.auth.data)
        ? state.auth.data.hasUserRecord
        : false,
});

const mapDispatchToProps = (dispatch: redux.Dispatch<AppState>): ConnectedDispatch => ({
    ...Comp.Base.DefaultDispatchPropsMap(dispatch),
    onAuthSuccess: (accessToken: string, refreshToken: string) => {
        dispatch(Actions.Auth.postAuthorize(accessToken, refreshToken));
    },
});

type OwnProps = RouteComponentProps<{}> & {
};

type UsernameStatus = "active" | "inactive" | "notfound";

type ConnectedState = Comp.Base.DefaultConnectedState & {
    clientId: string;
    clientSecret: string;
    isLoggedIn: boolean;
};

type ConnectedDispatch = Comp.Base.DefaultConnectedDispatch & {
    onAuthSuccess: (accessToken: string, refreshToken: string) => void;
};

interface OwnState {
    loading: boolean;
    usernameStatus: UsernameStatus;
    user?: M.User.UserContactProfile;
}

class CreateAccountComponent extends ComponentWithValidatedForm<ConnectedState & ConnectedDispatch & OwnProps, OwnState> {

    private _isMounted = false;
    private _emailCheckTimer?: NodeJS.Timer;
    private _searchTimeout: NodeJS.Timer;

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.Account.CreateAccount();
        this.state = {
            ...this.getInitialState(formModel),
            loading: false,
            usernameStatus: "notfound",
        };
    }

    public componentDidMount() {
        this._isMounted = true;

        if (this.props.isLoggedIn && !this.props.tokenData.requiresNewAccount) {
            this.props.notifyInfo("You already have an account");
            this.props.goto(this.getReturnUrl());
        }

        const userGuid = this.getUserGuid();
        if (Utility.isGuid(userGuid)) {
            this.setState({ ...this.state, loading: true });
            this.loadUserInfo(userGuid);
        }
    }

    public componentWillUnmount() {
        this._isMounted = false;
    }

    public render() {
        let usernameMsg = null;
        if (this.state.usernameStatus === "active")
            usernameMsg = <Comp.Editors.FormField name="">
                    <BS.Alert bsStyle="info">
                        <Comp.Generic.Icon icon="info-circle" /> This username is already in use. If this is your username,
                        please <Link to={Paths.Account.Login + "?returnUrl=" + this.getReturnUrl()}><strong>sign in</strong></Link> to continue.
                    </BS.Alert>
                </Comp.Editors.FormField>;
        else if (this.state.usernameStatus === "inactive")
            usernameMsg = <Comp.Editors.FormField name="">
                    <BS.Alert bsStyle="info">
                        <Comp.Generic.Icon icon="info-circle" /> This username is already in use, but the account is inactive. Please contact ASRS for assistance.
                    </BS.Alert>
                </Comp.Editors.FormField>;

        return <Comp.Page.PublicPage style={{ position: "relative" }}>
            <Comp.Generic.LoadingSpinner show={this.state.loading} />

            <Comp.Validation.ValidatedForm
                formComponent={this}
                model={this.state.formModel.data}
                onInputChange={(inputName) => this.handleInputChange(inputName)}
                onValidSubmit={async (model) => await this.onSubmit(model)}
                schema={this.state.formModel.rules}
                ref={(ref: any) => this.form = ref}
            >
                <h1>Create your account</h1>

                <Comp.Generic.RequiredFields />

                <h2>Name and position</h2>

                <Comp.Editors.FullNameSuffixField formComponent={this} name="name" />
                <Comp.Editors.TextField formComponent={this} name="degrees" placeholder="Examples: MD, MBA, PhD, etc." />
                <Comp.Editors.TextField formComponent={this} name="title" />
                <Comp.Editors.SelectField
                    formComponent={this}
                    isAsync
                    name="organization"
                    // onInputChange={(inputName, value) => this.onOrgChange(value)}
                    select={{
                        className: "txt-med",
                        ignoreAccents: false,
                        ignoreCase: false,
                        loadOptions: (search: string) => this.searchOrg(search),
                        placeholder: "",
                    }}
                />
                <Comp.Editors.TextField formComponent={this} name="department" />

                <hr />
                <h2>Contact information</h2>

                <Comp.Editors.AddressField formComponent={this} name="address" />
                <Comp.Editors.PhoneNumberField formComponent={this} name="phone" promptExtn />

                <hr />
                <h2>Account login</h2>

                <Comp.Editors.EmailField
                    formComponent={this}
                    name="username"
                    disabled={this.state.user !== undefined}
                    onInputChange={(inputName: string, value: string) => this.onUsernameChange(inputName, value)}
                />

                {usernameMsg}

                <Comp.Editors.PasswordField formComponent={this} name="password" />
                <Comp.Editors.PasswordField formComponent={this} name="confirmPassword" />

                <hr />
                <p>
                    By creating this account you confirm your intent to become a registered non-member with the ASRS.
                    As a registered non-member you may manage basic profile information, submit poster abstracts for the ASRS Annual Meeting,
                    register for ASRS sponsored events, and track related CME.
                </p>

                <Comp.Editors.SubmitField showProcessing={this.state.formProcessing} />

            </Comp.Validation.ValidatedForm>
        </Comp.Page.PublicPage>;
    }

    private handleInputChange(inputName: string) {
        if (inputName === "organization") {
            const formModelData = this.getFormModelData<Forms.Account.CreateAccountData>();
            this.onOrgChange(formModelData.organization as M.Org.BasicOrganizationProfile);
        }
    }

    private async checkUsername(username: string) {
        const qp = { queryParams: { username } };
        const result = await this.props.api.get<M.User.BasicProfile>(Api.endpoints.user.basicProfile, qp);

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            // a user is found
            const user = result.data;
            if (user.statusId === Statuses.Active.id) {
                // active user
                this.setState({ ...this.state, usernameStatus: "active" });
            }
            else {
                // inactive user
                this.setState({ ...this.state, usernameStatus: "inactive" });
            }
        }
        else
            this.setState({ ...this.state, usernameStatus: "notfound" });
    }

    private getUserGuid(): string {
        return QueryString.parse(this.props.location.search)["user"];
    }

    private getReturnUrl(): string {
        const queryString = QueryString.parse(this.props.location.search);
        return queryString["returnUrl"] || Paths.Home;
    }

    private isForAbstracts(): boolean {
        return QueryString.parse(this.props.location.search)["forAbstracts"] === "true";
    }

    private async loadUserInfo(userGuid: string) {
        const qp = { queryParams: { userGuid, includeContact: true }};
        const result = await this.props.api.get(Api.endpoints.user.basicProfile, qp) as M.Result.IResult<M.User.UserContactProfile>;

        if (!this._isMounted)
            return;

        if (result.isSuccess && result.data) {
            const user = result.data;
            if (!user.requiresNewAccount) {
                this.props.goto(Paths.Helper.Login(this.getReturnUrl()));
                return;
            }
            this.setFormDataFromUser(user);
        }

        this.setState({ ...this.state, loading: false });
    }

    private onOrgChange(record: M.Org.BasicOrganizationProfile | null) {
        const formModel = Object.assign({}, this.state.formModel) as Forms.Account.CreateAccount;
        formModel.data.organizationId = record === null ? 0 : record.id;
        this.setState({ ...this.state, formModel });
    }

    private onUsernameChange(inputName: string, value: string) {
        if (this._emailCheckTimer !== undefined)
            clearTimeout(this._emailCheckTimer);

        this._emailCheckTimer = setTimeout(() => {
            // quick validate username field
            const fieldSchema = yup.reach(this.state.formModel.rules, inputName);
            fieldSchema.validate(value, { abortEarly: false })
                .then(async (model) => {
                    // ajax username check
                    await this.checkUsername(value);
                })
                .catch((err) => {
                    // invalid email address
                });
        }, 375);
    }

    private async onSubmit(model: Forms.Account.CreateAccountData) {
        this.toggleProcessing(true);

        const body = {
            name: {
                firstName: model.name.first,
                middleName: model.name.middle,
                lastName: model.name.last,
                suffix: model.name.suffix,
            },
            degrees: model.degrees,
            department: model.department,
            existingUserId: model.existingUserId,
            forAbstracts: this.isForAbstracts(),
            organization: model.organization
                ? (model.organization as M.Generic.ReactSelectOption).label
                : undefined,
            organizationId: model.organization
                ? (model.organization as M.Generic.ReactSelectOption).value
                : undefined,
            title: model.title,
            address: {
                contactTypeId: model.address.contactTypeId,
                address1: model.address.address1,
                address2: model.address.address2,
                address3: model.address.address3,
                city: model.address.city,
                state: model.address.state,
                postalCode: model.address.postalCode,
                countryCode: (model.address.countryCode as any).value,
            },
            phone: {
                contactTypeId: model.phone.contactTypeId,
                number: model.phone.number,
                extn: model.phone.extension,
                countryCode: (model.phone.countryCode as any).value,
            },
            username: model.username,
            password: model.password,
            confirmPassword: model.confirmPassword,
        };

        const result = await this.props.api.post<boolean>(Api.endpoints.account.createAccount, { body: JSON.stringify(body) });

        if (!this._isMounted)
            return;

        if (result.isSuccess) {
            // now we need to log in the user
            const authResult = await this.props.api.authorize({
                clientId: this.props.clientId,
                clientSecret: this.props.clientSecret,
                username: model.username,
                password: model.password,
                grantType: "password",
            });

            // authorize legacy
            await new LegacyApi().authorize(model.username, model.password);

            if (!this._isMounted)
                return;

            if (authResult.isSuccess && authResult.data) {
                AuthHelper.saveTokenToCookie(authResult.data.access_token, authResult.data.refresh_token);
                this.props.notifySuccess("Your account has been successfully created.");
                this.props.onAuthSuccess(authResult.data.access_token, authResult.data.refresh_token);
                this.props.goto(this.getReturnUrl());
            }
            else {
                this.setFormErrors(M.Result.Result.errorsOrMessage(authResult), undefined, true);
            }
        }
        else {
            this.setFormErrors(M.Result.Result.errorsOrMessage(result), undefined, true);
        }

        if (this._isMounted)
            this.toggleProcessing(false);
    }

    private searchOrg(search: string) {
        if (!search || search.length < 2)
            return Promise.resolve({ options: [] });

        return new Promise((resolve, reject) => {
            // delay the ajax call as to not make a call for each letter typed.
            if (this._searchTimeout !== undefined)
                clearTimeout(this._searchTimeout);

            this._searchTimeout = setTimeout(() => {
                const queryParams = {
                    queryParams: {
                        query: search,
                        includeNonMembers: false,
                    },
                };

                // for adding new value
                const newOrg: M.Org.BasicOrganizationProfile & M.Generic.ReactSelectOption =
                    Utility.toReactSelectOption({ id: 0, statusId: 0, name: search }, search, 0);

                this.props.api.callApi<M.Org.BasicOrganizationProfile[]>(Api.endpoints.org.search, queryParams)
                    .then((result) => {
                        if (result.isSuccess && result.data) {
                            let options = result.data.map((o) => {
                                return Utility.toReactSelectOption(o, o.name, o.id);
                            });

                            const newOrgMatch = _.find(options, (org) => {
                                return org.name.toLowerCase().trim() === newOrg.label.toLowerCase().trim();
                            });

                            if (newOrgMatch === undefined)
                                options = [newOrg, ...options];

                            options = [...options];
                            resolve({ options });
                        }
                        else
                            throw new Error(result.errors.join("; "));
                    });
            }, 375);
        }).then((data) => {
            return data;
        });
    }

    private setFormDataFromUser(user: M.User.UserContactProfile) {
        const formModel = this.getFormModel() as Forms.Account.CreateAccount;
        formModel.data.degrees = user.degrees || "";
        formModel.data.existingUserId = user.id;
        formModel.data.name.first = user.firstName || "";
        formModel.data.name.last = user.lastName || "";
        formModel.data.name.middle = user.middleName || "";
        formModel.data.title = user.title || "";
        formModel.data.username = user.username || "";

        if (user.contacts.length)
            formModel.data.address = Forms.Generic.ContactModelToForm(user.contacts[0]);

        if (user.phones.length)
            formModel.data.phone = Forms.Generic.PhoneModelToForm(user.phones[0]);

        if (user.organizations.length) {
            formModel.data.organization = M.Org.OrganizationModelToForm(user.organizations[0]);
            formModel.data.organizationId = user.organizations[0].organizationId;
        }

        this.updateFormModel(formModel, () => {
            this.setState({ ...this.state, user });
        });
    }
}

export const CreateAccount: React.ComponentClass<OwnProps> = connect(mapStateToProps, mapDispatchToProps)(withRouter(CreateAccountComponent) as any);



// WEBPACK FOOTER //
// ./src/containers/account/createAccount/CreateAccount.tsx